import styles from '$components/SidePanel/SidePanelFile/SidePanelFile.module.css';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { PROJECT_STATUS } from '@he-novation/config/constants/projects.constants';
import { CustomFieldValues } from '@he-novation/config/types/custom-field.types';
import { FullFolder } from '@he-novation/config/types/folder.types';
import { Loader } from '@he-novation/design-system/components/widgets/Loader/Loader';
import { Tabs } from '@he-novation/design-system/components/widgets/Tabs/Tabs';
import { asyncFileCustomFieldsFetch } from '@he-novation/front-shared/async/file.async';
import { fetchFolder } from '@he-novation/front-shared/async/folder.async';
import { FrontFolderContentFile } from '@he-novation/front-shared/types/file.front-types';
import { FrontFolderContentFolder } from '@he-novation/front-shared/types/folder.front-types';
import { useAtomValue } from 'jotai';
import { useAtom } from 'jotai/index';

import { selectedFolderChildrenAtom } from '$atoms/selection-atoms';
import { workspaceNameAtom } from '$atoms/workspace-atoms';
import { SidePanelFileInfo } from '$components/SidePanel/SidePanelFile/SidePanelFileInfo/SidePanelFileInfo';
import { SidePanelFolder } from '$components/SidePanel/SidePanelFolder/SidePanelFolder';
import { useFolderContent } from '$hooks/useFolderContent';
import { useTranslate } from '$hooks/useTranslate';
import { folderSelector } from '$redux/content/folder/folderSelectors';
import { currentProjectSelector } from '$redux/content/projects/projectsSelector';

type SidePanelFolderItemProps = {
    payload?: {
        item: FrontFolderContentFile | FrontFolderContentFolder;
    };
};

export function SidePanelFolderItem({ payload }: SidePanelFolderItemProps) {
    const { t } = useTranslate();

    const workspaceName = useAtomValue(workspaceNameAtom);
    const [selected, setSelected] = useAtom(selectedFolderChildrenAtom);

    const { folderContent } = useFolderContent();

    const currentFolder = useSelector(folderSelector);
    const { currentProject } = useSelector(currentProjectSelector());

    const [selectedItem, setSelectedItem] = useState(payload?.item);
    const [folder, setFolder] = useState<FullFolder>();
    const [file, setFile] = useState<FrontFolderContentFile>();
    const [fileCustomFields, setFileCustomFields] = useState<CustomFieldValues>({});
    const [isLoading, setIsLoading] = useState<boolean>(false);

    useEffect(() => {
        if (payload?.item.uuid) {
            setSelected([payload.item.uuid]);
        }
    }, [payload?.item.uuid]);

    useEffect(() => {
        if (selected.length === 1) {
            setSelectedItem(
                (folderContent || []).find((i) => selected[0] === i.uuid) as
                    | FrontFolderContentFile
                    | FrontFolderContentFolder
            );
        } else {
            setSelectedItem(undefined);
        }
    }, [folderContent, selected]);

    useEffect(() => {
        if (!selectedItem) return;
        setIsLoading(true);
        asyncFileCustomFieldsFetch(workspaceName, selectedItem.uuid)
            .then(setFileCustomFields)
            .finally(() => setIsLoading(false));
    }, [selectedItem]);

    useEffect(() => {
        if (selectedItem) {
            switch (selectedItem?.type) {
                case 'file': {
                    setFile(selectedItem);
                    setFolder(undefined);
                    break;
                }
                case 'folder': {
                    setIsLoading(true);
                    fetchFolder(workspaceName, selectedItem)
                        .then(setFolder)
                        .finally(() => setIsLoading(false));
                    break;
                }
            }
        } else if (currentFolder) {
            setFolder(currentFolder);
        }
    }, [selectedItem, currentFolder]);

    if (isLoading) return <Loader />;

    if (folder) return <SidePanelFolder folder={folder} />;

    if (file)
        return (
            <Tabs
                className={styles.tabs}
                tabs={[
                    {
                        id: 'side-panel-tab-infos',
                        label: t('common.Information'),
                        icon: 'info'
                    }
                ]}
            >
                <SidePanelFileInfo
                    {...file}
                    fileCustomFields={fileCustomFields}
                    setFileCustomFields={setFileCustomFields}
                    readonly={currentProject?.status === PROJECT_STATUS.ARCHIVED}
                />
            </Tabs>
        );

    return null;
}
